import React, { useRef, useState } from 'react'
import moment from 'moment'
import DiaryDatePicker from '../../../../../Diary/DiaryDatePicker'
import { hapticTick } from '../../../../../../_utils/haptics'
import { dayLabel, postingDay, isoDay, parseIso } from '../../data/dates'
import AutoGrow from '../AutoGrow'
import { PlusIcon, TrashIcon, CalendarIcon } from '../icons'

// Headlines may break onto a new line where the salon wants; short fields
// (a name, a time) keep Enter from adding a line
const MULTILINE = ['headline', 'title', 'value', 'quote', 'body', 'line', 'thought']
const isMultiline = (field) => field.type === 'textarea' || MULTILINE.includes(field.key)

// A day, picked on the diary's own calendar. Days gone by can't be picked.
export const DayField = ({ id, label, value, onChange, hint, post }) => {
  const [open, setOpen] = useState(false)
  const [refused, setRefused] = useState(false)
  const anchor = useRef(null)
  const reads = value ? dayLabel(value, post ? postingDay(post) : new Date()) : ''
  return (
    <div className="sx-field" id={id}>
      <span className="sx-label">{label}</span>
      <button type="button" ref={anchor} className="sx-input sx-picker-btn" onClick={() => { hapticTick(); setOpen(true) }} aria-haspopup="dialog">
        <CalendarIcon size={17} />
        <span>{value ? moment(value).format('dddd D MMMM') : 'Pick a day'}</span>
      </button>
      <DiaryDatePicker
        open={open}
        selected={parseIso(value) || new Date()}
        anchorEl={anchor.current}
        onChange={(d) => {
          setOpen(false)
          if (moment(d).isBefore(moment(), 'day')) { setRefused(true); return }
          setRefused(false)
          onChange(isoDay(d))
        }}
        onClose={() => setOpen(false)}
      />
      <span className={`sx-hint${refused ? ' is-warn' : ''}`}>
        {refused ? 'That day has been and gone. Pick today or later.' : reads ? `Reads as “${reads}” on the day you post.` : hint}
      </span>
    </div>
  )
}

export const Field = ({ field, value, onChange, locked, post }) => {
  const id = `sx-f-${field.key}`
  if (field.type === 'stars') {
    const n = Number(value) || 5
    return (
      <div className="sx-field">
        <span className="sx-label">{field.label}</span>
        <div className={`sx-stars${locked ? ' is-locked' : ''}`} role="radiogroup" aria-label={field.label} aria-readonly={locked || undefined}>
          {[1, 2, 3, 4, 5].map(i => (
            <button key={i} type="button" role="radio" aria-checked={n === i} disabled={locked} className={`sx-star${i <= n ? ' is-on' : ''}`} onClick={() => { hapticTick(); onChange(i) }} aria-label={`${i} ${i === 1 ? 'star' : 'stars'}`}>★</button>
          ))}
        </div>
        {locked && <span className="sx-hint">The stars they gave, so they can’t be changed.</span>}
      </div>
    )
  }
  if (field.type === 'toggle') {
    return (
      <label className="sx-toggle" htmlFor={id}>
        <input id={id} type="checkbox" checked={!!value} onChange={(e) => { const v = e.target.checked; hapticTick(); onChange(v) }} />
        <span className="sx-toggle-track" aria-hidden><span /></span>
        <span className="sx-label">{field.label}</span>
      </label>
    )
  }
  if (field.type === 'date') {
    return <DayField id={id} label={field.label} value={value} onChange={onChange} hint={field.hint} post={post} />
  }
  if (field.type === 'list') {
    const list = Array.isArray(value) ? value : []
    const max = field.max || 3
    return (
      <div className="sx-field" id={id}>
        <span className="sx-label">{field.label}</span>
        <div className="sx-list">
          {list.map((v, i) => (
            <div className="sx-list-row" key={i}>
              <input className="sx-input" value={v} placeholder={field.placeholder} aria-label={`${field.itemLabel || 'Item'} ${i + 1}`} onChange={(e) => { const val = e.target.value; onChange(list.map((x, k) => (k === i ? val : x))) }} />
              <button type="button" className="sx-icon-btn" onClick={() => onChange(list.filter((_, k) => k !== i))} aria-label={`Remove ${(field.itemLabel || 'item').toLowerCase()} ${i + 1}`}><TrashIcon /></button>
            </div>
          ))}
          {list.length < max
            ? <button type="button" className="sx-add" onClick={() => onChange([...list, ''])}><PlusIcon size={16} />Add {(field.itemLabel || 'item').toLowerCase()}</button>
            : <span className="sx-hint">{field.fullHint || 'Three fit best. Switch on “Say there are more times” for the rest.'}</span>}
        </div>
      </div>
    )
  }
  // Pairs: a question and its answer ("My approach" / "Time to listen")
  if (field.type === 'pairs') {
    const list = Array.isArray(value) ? value : []
    const max = field.max || 3
    const set = (i, patch) => onChange(list.map((x, k) => (k === i ? { ...x, ...patch } : x)))
    return (
      <div className="sx-field" id={id}>
        <span className="sx-label">{field.label}</span>
        <div className="sx-list">
          {list.map((it, i) => (
            <div className="sx-pair-row" key={i}>
              <input className="sx-input sx-pair-q" value={it.q || ''} placeholder="My approach" aria-label={`Question ${i + 1}`} onChange={(e) => { const v = e.target.value; set(i, { q: v }) }} />
              <input className="sx-input sx-pair-a" value={it.a || ''} placeholder="Time to listen" aria-label={`Answer ${i + 1}`} onChange={(e) => { const v = e.target.value; set(i, { a: v }) }} />
              <button type="button" className="sx-icon-btn" onClick={() => onChange(list.filter((_, k) => k !== i))} aria-label={`Remove ${i + 1}`}><TrashIcon /></button>
            </div>
          ))}
          {list.length < max && <button type="button" className="sx-add" onClick={() => onChange([...list, { q: '', a: '' }])}><PlusIcon size={16} />Add one</button>}
          {field.hint && <span className="sx-hint">{field.hint}</span>}
        </div>
      </div>
    )
  }
  if (field.type === 'priceList') {
    const list = Array.isArray(value) ? value : []
    const max = field.max || 12
    const set = (i, patch) => onChange(list.map((x, k) => (k === i ? { ...x, ...patch } : x)))
    return (
      <div className="sx-field" id={id}>
        <span className="sx-label">{field.label}</span>
        <div className="sx-list">
          {list.map((it, i) => (
            <div className="sx-price-row" key={i}>
              <AutoGrow singleLine className="sx-input sx-price-name" value={it.name || ''} placeholder="Service" aria-label={`Service ${i + 1}`} onChange={(v) => set(i, { name: v })} />
              <input className="sx-input sx-price-price" value={it.price || ''} placeholder="£" aria-label={`Price ${i + 1}`} onChange={(e) => { const v = e.target.value; set(i, { price: v }) }} />
              <input className="sx-input sx-price-detail" value={it.detail || ''} placeholder="Time or note" aria-label={`Note ${i + 1}`} onChange={(e) => { const v = e.target.value; set(i, { detail: v }) }} />
              <button type="button" className="sx-icon-btn" onClick={() => onChange(list.filter((_, k) => k !== i))} aria-label={`Remove service ${i + 1}`}><TrashIcon /></button>
            </div>
          ))}
          {list.length < max && <button type="button" className="sx-add" onClick={() => onChange([...list, { name: '', price: '', detail: '' }])}><PlusIcon size={16} />Add a service</button>}
          {field.hint && <span className="sx-hint">{field.hint}</span>}
        </div>
      </div>
    )
  }
  return (
    <label className="sx-field" htmlFor={id}>
      <span className="sx-label">{field.label}</span>
      <AutoGrow
        id={id}
        singleLine={!isMultiline(field)}
        minRows={field.type === 'textarea' ? 2 : 1}
        value={value}
        placeholder={field.placeholder}
        readOnly={locked}
        onChange={onChange}
      />
      {(locked || field.hint) && <span className="sx-hint">{locked ? 'This is their review word for word, so it can’t be changed.' : field.hint}</span>}
    </label>
  )
}
